import { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { navLinks, LOGO_URL } from "../data/mock";
import { Button } from "./ui/button";
import { Menu, X, MessageCircle, ChevronDown } from "lucide-react";
import { useStorePicker } from "../hooks/useStorePicker";

export const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [openDropdown, setOpenDropdown] = useState(null);
  const { open: openStorePicker } = useStorePicker();
  const location = useLocation();
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 20);
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleNav = (href) => {
    setMobileMenuOpen(false);
    setOpenDropdown(null);
    if (href.startsWith("/#")) {
      const hash = href.slice(1);
      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(() => {
          const el = document.querySelector(hash);
          if (el) el.scrollIntoView({ behavior: "smooth" });
        }, 100);
      } else {
        const el = document.querySelector(hash);
        if (el) el.scrollIntoView({ behavior: "smooth" });
      }
    } else if (href.startsWith("#")) {
      const el = document.querySelector(href);
      if (el) el.scrollIntoView({ behavior: "smooth" });
    } else {
      navigate(href);
    }
  };

  return (
    <header
      className={`sticky top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? "bg-white/95 backdrop-blur-2xl shadow-lg shadow-sky-900/5 border-b border-sky-100"
          : "bg-white/70 backdrop-blur-sm"
      }`}
    >
      <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          <Link to="/" className="flex items-center gap-1 group" onClick={() => setMobileMenuOpen(false)}>
            <img src={LOGO_URL} alt="Blitzwash Laundry" className="h-12 lg:h-14 w-auto object-contain mix-blend-multiply" />
          </Link>

          <nav className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <div key={link.href} className="relative"
                   onMouseEnter={() => link.isDropdown && setOpenDropdown(link.href)}
                   onMouseLeave={() => link.isDropdown && setOpenDropdown(null)}>
                <button
                  onClick={() => handleNav(link.href)}
                  className={`inline-flex items-center gap-1 px-3.5 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                    location.pathname === link.href ? "text-sky-700 bg-sky-50" : "text-slate-600 hover:text-sky-600 hover:bg-sky-50/60"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase()}`}
                >
                  {link.label}
                  {link.isDropdown && <ChevronDown className="w-3 h-3" />}
                </button>
                {link.isDropdown && openDropdown === link.href && (
                  <div className="absolute top-full left-0 pt-2 min-w-[220px]">
                    <div className="bg-white border border-slate-200 rounded-2xl shadow-xl shadow-sky-900/10 py-2">
                      {link.children.map((c) => (
                        <Link key={c.href} to={c.href} onClick={() => setOpenDropdown(null)}
                          className="block px-4 py-2 text-sm text-slate-600 hover:bg-sky-50 hover:text-sky-700 transition-colors"
                          data-testid={`nav-dropdown-${c.href}`}>
                          {c.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </nav>

          <div className="hidden lg:flex items-center gap-3">
            <button
              type="button"
              onClick={() => openStorePicker("whatsapp")}
              className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 hover:text-sky-600 px-3 py-2 rounded-lg hover:bg-sky-50/60 transition-colors"
              data-testid="header-whatsapp-btn"
            >
              <MessageCircle className="w-4 h-4" />
              WhatsApp
            </button>
            <Button
              className="bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/25 hover:shadow-sky-600/40"
              onClick={() => handleNav("/#contact")}
            >
              Schedule Pickup
            </Button>
          </div>

          <button
            className="lg:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X className="w-5 h-5 text-slate-700" /> : <Menu className="w-5 h-5 text-slate-700" />}
          </button>
        </div>
      </div>

      <div className={`lg:hidden overflow-hidden transition-all duration-300 ease-in-out ${mobileMenuOpen ? "max-h-[700px] opacity-100" : "max-h-0 opacity-0"}`}>
        <div className="bg-white/95 backdrop-blur-xl border-t border-sky-100 px-5 pb-5 pt-2">
          <nav className="flex flex-col gap-1">
            {navLinks.map((link) => (
              <div key={link.href}>
                <button
                  onClick={() => handleNav(link.href)}
                  className="w-full text-left px-4 py-3 text-sm font-medium text-slate-600 hover:bg-sky-50 hover:text-sky-600 rounded-xl transition-colors"
                >
                  {link.label}
                </button>
                {link.isDropdown && (
                  <div className="pl-4">
                    {link.children.map((c) => (
                      <Link key={c.href} to={c.href} onClick={() => setMobileMenuOpen(false)}
                        className="block px-4 py-2 text-xs text-slate-500 hover:text-sky-600">
                        · {c.label}
                      </Link>
                    ))}
                  </div>
                )}
              </div>
            ))}
            <div className="pt-3 px-1 space-y-2">
              <button
                type="button"
                onClick={() => { setMobileMenuOpen(false); openStorePicker("whatsapp"); }}
                className="block w-full text-center py-3 border border-sky-200 text-sky-600 rounded-xl font-medium text-sm hover:bg-sky-50 transition-colors"
                data-testid="header-mobile-whatsapp-btn"
              >
                Chat on WhatsApp
              </button>
              <Button
                className="w-full bg-sky-600 hover:bg-sky-700 text-white shadow-lg shadow-sky-600/20"
                onClick={() => handleNav("/#contact")}
              >
                Schedule Pickup
              </Button>
            </div>
          </nav>
        </div>
      </div>
    </header>
  );
};
